import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  Layout,
  Button,
  Row,
  Col,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLocation, useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import IndFlag from '../../../../assets/IndFlag.png';
import PakFlag from '../../../../assets/pakFlag.png';
import MobileHeader from '../../../../components/Header/components/mobileHeader';
import RecaptchaModal from '../recaptcha';
import Profile from '../Profile';
import PaymentPortal from '../PaymentPortal';
import LandingScreen from './component/LandingScreen';
import SetAmountScreen from './component/SetAmountScreen';
import PayScreen from './component/PayScreen';
import QuickPayMessageModal from './component/MessageModal';
import { preloadDeviceFingerprint } from '../../../../services/deviceFingerprint';

type Props = {
  enableNormalFlow: Function,
  shopEntity: Object,
  redirectUrl: String,
  noMenuItems: Boolean,
};

const QuickPay = (props: Props) => {
  const {
    enableNormalFlow,
    shopEntity,
    redirectUrl,
    noMenuItems,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  const slugName = pathname.replace("/", "");
  const [storeOpen, setStoreOpen] = useState(true);
  const [enableHOP, setEnableHOP] = useState(false);
  const [bannerImage, setBannerImage] = useState('');
  const [bannerText, setBannerText] = useState('');
  const [currencyType, setCurrencyType] = useState('');
  const [rowMarginTop, setRowMarginTop] = useState('25vh');
  const [rowPaddingX, setRowPaddingX] = useState('40px');
  const buttonStyle = {
    fontSize: '20px',
    fontWeight: '600',
    backgroundColor: '#1e4a72',
    borderColor: '#1e4a72',
    borderRadius: '8px',
  };
  const [quickPayOnly, setQuickPayOnly] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [payableAmount, setPayableAmount] = useState('1.00');
  const [notes, setNotes] = useState('');
  const [paymentPortalModal, setPaymentPortalModal] = useState(false);
  const [recaptchaModalVisible, setRecaptchaModalVisible] = useState(false);
  const [savedPaymentDetails, setSavedPaymentDetails] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [paymentPortalInfo, setPaymentPortalInfo] = useState({});
  const [messageModalVisible, setMessageModalVisible] = useState(false);
  const [statusModalMessage, setStatusModalMessage] = useState('');
  const [isProfileVisible, setIsProfileVisible] = useState(false);
  const [paymentAtStoreData, setPaymentAtStoreData] = useState(null);
  const [showCustomerPhoneNum, setShowCustomerPhoneNum] = useState(false);
  const [customerPhoneNum, setCustomerPhoneNum] = useState({
    callingCode: null,
    phone: null,
  });
  const singleUsePromoCodeValue = useRef(null);
  const currentPromoCode = useRef(null);
  const currentCustomerPhoneNum = useRef(null);

  const displayRecaptcha = (data) => {
    setSavedPaymentDetails(data);
    setRecaptchaModalVisible(true);
  };

  const onGlobalCaptchaVerified = (token) => {
    setRecaptchaToken(token);
    setRecaptchaModalVisible(false);
  };

  const handlePaymentPortalInfo = useCallback((value) => {
    setPaymentPortalInfo({
      inventoryGid: value.inventoryGid,
      splitUpInfo: value.splitUpInfo,
      selectedAccount: value.selectedAccount,
    });
  }, []);

  useEffect(() => {
    preloadDeviceFingerprint();
  }, []);

  useEffect(() => {
    switch (currentStep) {
      case 0:
        setRowMarginTop('25vh');
        setRowPaddingX('40px');
        break;
      case 2:
        setRowMarginTop('5vh');
        setRowPaddingX('0px');
        break;
      default:
        setRowMarginTop('15vh');
        setRowPaddingX('40px');
    }
  }, [currentStep]);

  useEffect(() => {
    setQuickPayOnly(noMenuItems);
  }, [noMenuItems]);

  const getStoreTimings = (timings, selectedAccount) => {
    const today = moment.utc(new Date()).tz(selectedAccount && selectedAccount.timezone).format('MMM DD, YYYY, hh:mm a');
    const days = [
      "SUNDAY",
      "MONDAY",
      "TUESDAY",
      "WEDNESDAY",
      "THURSDAY",
      "FRIDAY",
      "SATURDAY",
    ];
    const day = days[new Date(today).getDay()];
    const minutes = new Date(today).getHours() * 60 + new Date(today).getMinutes();
    const currentDay =
      timings && timings.filter((weekDay) => weekDay.dayOfWeek === day);
      if (currentDay && currentDay[0] && currentDay[0].operatingHours && currentDay[0].operatingHours.length > 1) {
        const array = [...currentDay[0].operatingHours];
        const tempArray = [];
        for (let i = 0; i < array.length; i += 1) {
          if (array[i].openingTime < minutes && array[i].closingTime >= minutes) {
            tempArray.push(true);
          }
        }
        tempArray.filter((item) => item === true);
        if (tempArray && tempArray.length !== 0) {
          return true;
        } else {
          return false;
        }
      }
    return currentDay && currentDay[0] && currentDay[0].operatingHours[0] &&
      currentDay[0].operatingHours[0].openingTime < minutes &&
      currentDay[0].operatingHours[0].closingTime >= minutes
  };

  const getAccountDetails = async () => {
    if (!shopEntity) return;
    try {
      const selectedAccount = shopEntity && shopEntity.account ? shopEntity.account : null;
      const timings = shopEntity && shopEntity.hoursOfOperations;
      setCurrencyType(shopEntity?.account?.currency?.prefix ?? '');
      setEnableHOP(shopEntity.enableHoursOfOperation);
      setBannerImage(shopEntity.banner ? shopEntity.banner : '');
      if (timings && shopEntity.enableHoursOfOperation) {
        const storeOpenStatus = getStoreTimings(timings, selectedAccount);
        setStoreOpen(storeOpenStatus);
      }
      setBannerText(shopEntity && shopEntity.bannerText ? shopEntity.bannerText : '');
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    getAccountDetails();
  }, [slugName, shopEntity]);

  const handlePaymentAtStore = async (data) => {
    setPaymentAtStoreData(data);
  };

  return (
    <Layout.Content
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000,
        background: '#fff',
        padding: 24,
        margin: 0,
        height: '100vh',
        width: '100vw',
        padding: 0,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        overflowY: 'auto',
      }}
    >
      {/* Banner Section - Consistent across screens */}
      <Row
        justify="start"
        style={{
          padding: '0px',
          borderBottom: '1px solid #000000',
        }}
      >
        <Col span={4}>
          {currentStep !== 0 && (
            <Button
              type="ghost"
              onClick={() => setCurrentStep(state => state - 1)}
              icon={<ArrowLeftOutlined />}
              style={{
                height: '100%',
                width: '100%',
                fontSzie: '5vmin',
              }}
            />
          )}
        </Col>
        <Col
          span={16}
          style={{
            textAlign: 'center',
          }}
        >
          <MobileHeader
            slugName={slugName}
            bannerText={bannerText}
            bannerImage={bannerImage}
            enableHOP={enableHOP}
            storeOpen={storeOpen}
            IndFlag={IndFlag}
            PakFlag={PakFlag}
          />
        </Col>
      </Row>

      {/* Button Section - Changes based on screen */}
      <Row 
        gutter={[16, 16]} 
        justify="center"
        align="middle"
        flex={1}
        style={{ 
          padding: `0 ${rowPaddingX}`,
          marginTop: rowMarginTop,
        }}
      >
        {currentStep === 0 && (
          <LandingScreen
            enableNormalFlow={enableNormalFlow}
            setCurrentStep={setCurrentStep}
            quickPayOnly={quickPayOnly}
            buttonStyle={buttonStyle}
          />
        )}
        {currentStep === 1 && (
          <SetAmountScreen
            payableAmount={payableAmount}
            setPayableAmount={setPayableAmount}
            notes={notes}
            setNotes={setNotes}
            setCurrentStep={setCurrentStep}
            currencyType={currencyType}
            buttonStyle={buttonStyle}
          />
        )}
        {currentStep === 2 && (
          <PayScreen
            userSetAmount={payableAmount}
            currencyType={currencyType}
            notes={notes}
            shopEntity={shopEntity}
            isShopOpen={storeOpen}
            handlePaymentPortalInfo={handlePaymentPortalInfo}
            setMessageModalVisible={setMessageModalVisible}
            setStatusModalMessage={setStatusModalMessage}
            redirectUrl={redirectUrl}
            slugName={slugName}
            setIsProfileVisible={setIsProfileVisible}
            paymentAtStoreData={paymentAtStoreData}
            buttonStyle={buttonStyle}
            setPaymentPortalModal={setPaymentPortalModal}
            setCustomerPhoneNum={setCustomerPhoneNum}
            customerPhoneNum={customerPhoneNum}
            showCustomerPhoneNum={showCustomerPhoneNum}
            setShowCustomerPhoneNum={setShowCustomerPhoneNum}
            singleUsePromoCodeValue={singleUsePromoCodeValue}
            currentPromoCode={currentPromoCode}
            currentCustomerPhoneNum={currentCustomerPhoneNum}
          />
        )}
      </Row>
      <Row>
        <Col span={24}>
        {paymentPortalModal && (
          <PaymentPortal
            close={() => setPaymentPortalModal(false)}
            visible={paymentPortalModal}
            inventoryGid={paymentPortalInfo?.inventoryGid}
            totalPay={paymentPortalInfo?.splitUpInfo?.netPayable}
            selectedAccount={paymentPortalInfo?.selectedAccount}
            displayRecaptcha={displayRecaptcha}
            recaptchaToken={recaptchaToken}
            setRecaptchaToken={setRecaptchaToken}
            setSavedPaymentDetails={setSavedPaymentDetails}
            savedPaymentDetails={savedPaymentDetails}
            setIsProfileVisible={setIsProfileVisible}
            showCustomerPhoneNum={(singleUsePromoCodeValue?.current && currentCustomerPhoneNum?.current?.callingCode && currentCustomerPhoneNum?.current?.phone) || false}
            customerPhoneNum={currentCustomerPhoneNum?.current}
          />
        )}
        </Col>
        <Col span={24}>
          {recaptchaModalVisible && (
            <RecaptchaModal
              visible={recaptchaModalVisible}
              onVerified={onGlobalCaptchaVerified}
            />
          )}
        </Col>
        <Col span={24}>
          {isProfileVisible && (
            <Profile
              close={() => setIsProfileVisible(false)}
              visible={isProfileVisible}
              submit={handlePaymentAtStore}
              showCustomerPhoneNum={(singleUsePromoCodeValue?.current && currentCustomerPhoneNum?.current?.callingCode && currentCustomerPhoneNum?.current?.phone) || false}
              customerPhoneNum={currentCustomerPhoneNum?.current}
            />
          )}
        </Col>
        <Col span={24}>
          {messageModalVisible && (
            <QuickPayMessageModal
              statusModalMessage={statusModalMessage}
              messageModalVisible={messageModalVisible}
            />
          )}
        </Col>
      </Row>
    </Layout.Content>
  );
};

export default QuickPay;
